/**
 * "Use este acervo no seu agente", portado de app.js:376-442.
 *
 * É o cartão de adoção: quatro comandos e o Cursor passa a ler o acervo. Quem já
 * ligou o CLI não precisa do passo a passo ocupando o topo da doc, então o
 * estado recolhido fica guardado em `ce-agent-open` — a mesma chave do painel
 * atual, para ninguém ver o cartão reabrir depois do corte.
 */
import { useState } from 'react';
import { pegar } from '../api/cliente';
import { copiar, useRecado } from '../componentes/Recado';

const CHAVE_ABERTO = 'ce-agent-open';

export function trechoMcp(slug: string): string {
  return JSON.stringify({ mcpServers: { 'context-engine': { command: 'eng', args: ['mcp', slug] } } }, null, 2);
}

interface Pacote { project: string; head?: string | null; acervo_rev?: number | string | null }

export function promptDoAgente(pkg: Pacote): string {
  return [
    `Contexto do projeto ${pkg.project} (Context Engine · acervo no commit ${(pkg.head || '?').slice(0, 8)}, rev ${pkg.acervo_rev}).`,
    'Regras de uso: facts.endpoints e os blocos "Fatos (AST)" vêm do código no SHA e vencem qualquer prosa; as notas do time explicam o que o código não mostra (runtime, negócio, gotcha); a prosa gerada é rascunho.',
    'Cite unit_key e source_sha quando afirmar algo. Para o corpo de um arquivo, peça: eng doc <unit_key>.',
    'Este pacote veio do servidor e não conhece o working tree local.',
    '',
    '```json',
    JSON.stringify(pkg, null, 2),
    '```',
  ].join('\n');
}

export function CartaoDoAgente({ slug }: { slug: string }) {
  const avisar = useRecado();
  const [mini, setMini] = useState(() => localStorage.getItem(CHAVE_ABERTO) !== '1');
  const [ocupado, setOcupado] = useState<'' | 'pacote' | 'prompt'>('');
  const origem = window.location.origin;

  const copiarTexto = async (texto: string, recado: string) => {
    avisar(await copiar(texto) ? recado : 'não consegui copiar — selecione o texto e use Ctrl+C');
  };

  const alternar = () => {
    const novo = !mini;
    setMini(novo);
    localStorage.setItem(CHAVE_ABERTO, novo ? '0' : '1');
  };

  // botão que chama a rede avisa que está trabalhando; senão parece que não clicou
  const comPacote = async (qual: 'pacote' | 'prompt') => {
    setOcupado(qual);
    try {
      const p = await pegar<Pacote>(`/api/${slug}/context`);
      if (qual === 'pacote') {
        const texto = JSON.stringify(p, null, 2);
        await copiarTexto(texto, `pacote copiado · ${(texto.length / 1024).toFixed(1)} kB`);
      } else {
        await copiarTexto(promptDoAgente(p), 'prompt copiado — cole no agente');
      }
    } catch (e) {
      avisar(e instanceof Error ? e.message : String(e));
    } finally { setOcupado(''); }
  };

  const passo = (n: number, cmd: string) => (
    <div className="agent-step">
      <span className="n">{n}</span>
      <code>{cmd}</code>
      <button type="button" className="btn ghost sm" onClick={() => void copiarTexto(cmd, 'copiado')}>copiar</button>
    </div>
  );

  return (
    <div className={`agent-card${mini ? ' mini' : ''}`} id="agent-card">
      <div className="ac-head">
        <h4>Use este acervo no seu agente</h4>
        <span className="ac-why">4 comandos e o Cursor passa a ler o acervo do time</span>
        <button type="button" className="ac-tog" aria-expanded={!mini} onClick={alternar}>
          {mini ? 'mostrar como' : 'esconder'}
        </button>
      </div>
      <div className="ac-body">
        <div className="sub" style={{ marginTop: 9 }}>
          O CLI entrega o pacote em camadas — overview, rotas do AST, notas do time e índice de
          arquivos — dentro do Cursor, com <code>unit_key</code> e <code>source_sha</code> em cada
          afirmação. Sem copiar e colar.
        </div>
        <div className="agent-steps">
          {passo(1, `irm ${origem}/cli/install.ps1 | iex`)}
          {passo(2, 'eng login')}
          {passo(3, `eng use ${slug}`)}
          {passo(4, 'eng init-agent   # escreve .cursor/mcp.json e a regra do agente')}
        </div>
        <div className="sub" style={{ margin: '2px 0 8px' }}>
          Se preferir configurar na mão, o conteúdo do <code>.cursor/mcp.json</code> é este:
        </div>
        <pre style={{ margin: '0 0 12px', background: '#0d1626', borderRadius: 10, padding: '12px 14px', overflowX: 'auto' }}>
          <code style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: '#d6e0ef' }}>{trechoMcp(slug)}</code>
        </pre>
        <div className="agent-acts">
          <button type="button" className="btn ghost sm" onClick={() => void copiarTexto(trechoMcp(slug), 'copiado')}>
            Copiar mcp.json
          </button>
          <button type="button" className="btn sm" disabled={!!ocupado} onClick={() => void comPacote('pacote')}>
            {ocupado === 'pacote' ? 'montando…' : 'Copiar pacote para o agente'}
          </button>
          <button type="button" className="btn ghost sm" disabled={!!ocupado} onClick={() => void comPacote('prompt')}>
            {ocupado === 'prompt' ? 'montando…' : 'Copiar prompt pronto'}
          </button>
        </div>
        <div className="sub" style={{ margin: '10px 0 0' }}>
          O pacote copiado vem do servidor: ele não conhece seu HEAD local nem arquivos não
          commitados. Para frescor real, rode <code>eng context</code> no clone.
        </div>
      </div>
    </div>
  );
}
